import React, { useCallback, useEffect, useRef, useState, memo } from 'react';
import { isArray, isObject } from 'radash';
import Icon, { IconName } from '@/components/atoms/Icon';
import TooltipV2 from '@/components/TooltipV2';

interface Option {
  name: any;
  subText?: string;
  label?: string;
  action?: {
    icon?: IconName;
    label?: string;
    isVisible?: boolean;
    isDisabled?: boolean;
    isClickable?: boolean;
    onClick?: () => void;
  };
}

interface SelectOptionProps {
  option: Option | string;
  index: number;
  sectionIndex?: number;
  selected: string;
  onSelect: (value: string) => void;
  onKeyDown: (e: React.KeyboardEvent, value: string) => void;
  onOptionAction: (e: React.MouseEvent, action: Option['action']) => void;
}

const SelectOption = React.memo(
  ({ option, index, sectionIndex, selected, onSelect, onKeyDown, onOptionAction }: SelectOptionProps) => {
    const value = typeof option === 'string' ? option : option.name;
    const optionAction = typeof option === 'object' ? option.action : undefined;
    const uniqueKey = sectionIndex !== undefined ? `${sectionIndex}-${index}` : index;
    const isOptionObject = typeof option === 'object';

    const label = isOptionObject && typeof option.label === 'string' ? option.label : value;

    return (
      <div
        key={uniqueKey}
        role="menuitem"
        tabIndex={index}
        className="flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors hover:bg-gray-100"
        onClick={() => onSelect(value)}
        onKeyDown={e => onKeyDown(e, value)}
      >
        {selected === value && (
          <span className="absolute left-2 flex size-3.5 items-center justify-center">
            <Icon name="IconCheck" className="size-4" />
          </span>
        )}
        <div className={`flex items-center gap-1 ${!isOptionObject || !option.subText ? 'grow' : ''}`}>
          <span className="grow">{label}</span>
          {isOptionObject && option.subText && (
            <span className="ml-1 truncate text-xs text-slate-600">{option.subText}</span>
          )}
        </div>
        {optionAction?.isVisible !== false && (
          <TooltipV2
            tooltipId={`select-dropdown-option-tooltip-${uniqueKey}`}
            tooltipContent={!optionAction?.isDisabled && optionAction?.label ? optionAction.label : ''}
            tooltipElement={
              optionAction?.isClickable === false ? (
                optionAction?.icon && (
                  <div className="ml-2 rounded p-1">
                    <Icon name={optionAction.icon} size={16} />
                  </div>
                )
              ) : (
                <button
                  className={`ml-2 rounded p-1 hover:bg-white ${
                    optionAction?.isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                  }`}
                  data-testid="select-dropdown-option-action"
                  onClick={e => onOptionAction(e, optionAction)}
                  disabled={optionAction?.isDisabled}
                >
                  {optionAction?.icon && <Icon name={optionAction.icon} size={16} />}
                </button>
              )
            }
            place="bottom"
            size="xsmall"
            noArrow={true}
          />
        )}
      </div>
    );
  }
);

SelectOption.displayName = 'SelectOption';

interface DropdownSection {
  label: string;
  options: Option[] | string[];
}

interface SelectDropdownProps {
  options?: Option[] | string[];
  sections?: DropdownSection[];
  selected: string;
  label?: string;
  onChange: (selected: string) => void;
  placeholder?: string;
  buttonClassName?: string;
  className?: string;
  disabled?: boolean;
  menuClassName?: string;
  actionItem?: ActionItem;
}

interface ActionItem {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  isDisabled?: boolean;
}

const SelectDropdown: React.FC<SelectDropdownProps> = ({
  options,
  sections,
  selected,
  label,
  onChange,
  placeholder = 'Select...',
  className = '',
  buttonClassName = '',
  disabled = false,
  menuClassName = '',
  actionItem
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback((event: MouseEvent): void => {
    if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mouseup', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mouseup', handleClickOutside);
    };
  }, [isOpen, handleClickOutside]);

  const handleButtonClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!disabled) {
        setIsOpen(!isOpen);
      }
    },
    [disabled, isOpen]
  );

  const handleSelect = useCallback(
    (option: string): void => {
      onChange(option);
      setIsOpen(false);
    },
    [onChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, value: string) => {
      if (e.key === 'Enter' || e.key === ' ') {
        handleSelect(value);
      }
    },
    [handleSelect]
  );

  const handleActionItemClick = useCallback(
    (e: React.MouseEvent): void => {
      if (!actionItem?.isDisabled) {
        e.stopPropagation();
        actionItem?.onClick?.();
        setIsOpen(false);
      }
    },
    [actionItem]
  );

  const handleOptionAction = useCallback((e: React.MouseEvent, action: Option['action']) => {
    e.stopPropagation();
    if (action && !action.isDisabled && typeof action.onClick === 'function') {
      action.onClick();
    }
  }, []);

  const selectedOptionLabel =
    isArray(options) && options.length > 0 && isObject(options[0])
      ? ((options as Option[]).find(option => option.name === selected)?.label ?? null)
      : null;

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      <button
        data-testid="select-dropdown-button"
        className={`inline-flex h-9 w-full items-center justify-between gap-2 whitespace-nowrap rounded-lg border bg-white px-4 py-2 text-sm font-medium transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-theme-input-focus ${buttonClassName} ${
          disabled ? 'pointer-events-none opacity-50' : ''
        }`}
        onClick={handleButtonClick}
      >
        <span className="block truncate">{selectedOptionLabel ?? (selected || placeholder)}</span>
        <Icon name="IconChevronDown" className="size-4 opacity-50" />
      </button>

      {isOpen && !disabled && (
        <div
          className={`absolute left-0 top-full z-[9999] mt-1 w-max min-w-full rounded-md border bg-white p-1 shadow-md transition-all ${menuClassName}`}
        >
          {!sections && (
            <>
              {label && <div className="px-2 py-1.5 text-sm font-semibold">{label}</div>}

              {label && <hr className="-mx-1 my-1 h-px border-0 bg-gray-200" />}

              {options && options.length === 0 ? (
                <div className="px-2 py-1.5 text-sm text-gray-500">No options available</div>
              ) : (
                options?.map((option, index) => (
                  <SelectOption
                    key={index}
                    option={option}
                    index={index}
                    selected={selected}
                    onSelect={handleSelect}
                    onKeyDown={handleKeyDown}
                    onOptionAction={handleOptionAction}
                  />
                ))
              )}
            </>
          )}

          {/* Render sections if provided, otherwise render single options */}
          {sections?.map((section, sectionIndex) => (
            <div key={`section-${sectionIndex}`}>
              {/* Section Label */}
              <div className="px-2 py-1.5 text-sm font-semibold text-gray-700">{section.label}</div>

              {/* Section Options */}
              {section.options.length === 0 ? (
                <div className="px-2 py-1.5 text-sm text-gray-500">No options available</div>
              ) : (
                section.options.map((option, index) => (
                  <SelectOption
                    key={`${sectionIndex}-${index}`}
                    option={option}
                    index={index}
                    sectionIndex={sectionIndex}
                    selected={selected}
                    onSelect={handleSelect}
                    onKeyDown={handleKeyDown}
                    onOptionAction={handleOptionAction}
                  />
                ))
              )}

              {/* Separator between sections (except for last section) */}
              {sectionIndex < sections.length - 1 && <hr className="-mx-1 my-1 h-px border-0 bg-gray-200" />}
            </div>
          ))}

          {/* Action Item */}
          {actionItem && (
            <>
              <hr className="-mx-1 my-1 h-px border-0 bg-gray-200" />
              <div
                data-testid="select-dropdown-action-item"
                className={`relative flex w-full select-none items-center rounded-sm py-2.5 pl-8 pr-2 text-sm outline-none transition-colors hover:bg-gray-100 ${
                  actionItem?.isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                }`}
                onClick={handleActionItemClick}
              >
                <span className="absolute left-2 flex items-center justify-center">{actionItem.icon}</span>
                {actionItem.label}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default memo(SelectDropdown);
